import React from 'react';
import { compareTrendLabels, formatTrendAxisLabel, formatTrendLabel } from '../../utils/trendGranularity';

function getPointLabel(point) {
  return point?.date ?? point?.label ?? point?.period ?? '';
}

function getPointValue(point) {
  const value = point?.count ?? point?.value ?? 0;
  return Number.isFinite(Number(value)) ? Number(value) : 0;
}

function normalizeSeries(series) {
  if (!series) return [];
  if (Array.isArray(series)) return series;
  if (Array.isArray(series.data)) return series.data;
  return [];
}

const SERIES_CONFIG = [
  { key: 'created', label: 'Created', color: '#7d59a6' },
  { key: 'sent', label: 'Sent', color: '#4f9cf9' },
  { key: 'completed', label: 'Completed', color: '#2eb872' },
  { key: 'declined', label: 'Declined', color: '#ef6b6b' },
];

const CHART_HEIGHT = 260;
const PADDING = { top: 20, right: 16, bottom: 40, left: 44 };

function buildPath(points) {
  if (!points.length) return '';
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
}

const TeamTrendChart = ({ series = {}, granularity = 'month' }) => {
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const svgRef = React.useRef(null);

  const merged = React.useMemo(() => {
    const labelSet = new Set();
    SERIES_CONFIG.forEach(({ key }) => {
      normalizeSeries(series[key]).forEach((point) => {
        const label = getPointLabel(point);
        if (label) labelSet.add(label);
      });
    });
    return Array.from(labelSet)
      .sort((a, b) => compareTrendLabels(a, b, granularity))
      .map((label) => {
        const row = {
          label,
          displayLabel: formatTrendLabel(label, granularity),
          axisLabel: formatTrendAxisLabel(label, granularity),
        };
        SERIES_CONFIG.forEach(({ key }) => {
          const point = normalizeSeries(series[key]).find((p) => getPointLabel(p) === label);
          row[key] = point ? getPointValue(point) : 0;
        });
        return row;
      });
  }, [series, granularity]);

  const maxValue = React.useMemo(() => {
    if (!merged.length) return 1;
    return Math.max(
      1,
      ...merged.flatMap((row) => SERIES_CONFIG.map(({ key }) => row[key] || 0))
    );
  }, [merged]);

  const yTicks = React.useMemo(() => {
    const step = maxValue <= 5 ? 1 : Math.ceil(maxValue / 4);
    const ticks = [];
    for (let v = 0; v <= maxValue; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] !== maxValue) ticks.push(maxValue);
    return ticks;
  }, [maxValue]);

  const plotWidth = Math.max(480, merged.length * 48);
  const innerWidth = plotWidth - PADDING.left - PADDING.right;
  const innerHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const scaleX = React.useCallback((index) => (
    PADDING.left + (merged.length <= 1 ? innerWidth / 2 : (index / (merged.length - 1)) * innerWidth)
  ), [merged.length, innerWidth]);
  const scaleY = React.useCallback((value) => (
    PADDING.top + innerHeight - (value / maxValue) * innerHeight
  ), [innerHeight, maxValue]);

  const linePaths = React.useMemo(
    () =>
      SERIES_CONFIG.map(({ key }) => {
        const points = merged.map((row, index) => ({
          x: scaleX(index),
          y: scaleY(row[key] || 0),
          value: row[key] || 0,
        }));
        return { key, points, path: buildPath(points) };
      }),
    [merged, scaleX, scaleY]
  );

  const labelStep = Math.max(1, Math.ceil(merged.length / 8));

  const handleMouseMove = (event) => {
    if (!svgRef.current || merged.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relativeX = ((event.clientX - rect.left) / rect.width) * plotWidth;
    const index = Math.round(
      ((relativeX - PADDING.left) / innerWidth) * (merged.length - 1)
    );
    const clamped = Math.min(Math.max(index, 0), merged.length - 1);
    setHoverIndex(clamped);
  };

  if (!merged.length) {
    return (
      <div className="team-trend-chart team-trend-chart--empty">
        <i className="ri-line-chart-line" />
        <p>No trend data for this period.</p>
      </div>
    );
  }

  const activeRow = hoverIndex != null ? merged[hoverIndex] : null;

  return (
    <div className="team-trend-chart">
      <div className="team-trend-legend">
        {SERIES_CONFIG.map(({ key, label, color }) => (
          <span
            key={key}
            className="team-trend-legend-pill"
            style={{ '--pill-color': color, '--pill-soft': `${color}1f` }}
          >
            <span className="team-trend-legend-dot" />
            {label}
          </span>
        ))}
      </div>

      <div className="team-trend-chart-scroll">
        <svg
          ref={svgRef}
          className="team-trend-svg team-trend-svg--line"
          viewBox={`0 0 ${plotWidth} ${CHART_HEIGHT}`}
          preserveAspectRatio="xMinYMid meet"
          role="img"
          aria-label="Envelope trends line chart"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIndex(null)}
        >
          {yTicks.map((tick) => {
            const y = scaleY(tick);
            return (
              <g key={tick}>
                <line
                  x1={PADDING.left}
                  y1={y}
                  x2={plotWidth - PADDING.right}
                  y2={y}
                  className="team-trend-grid-line"
                />
                <text x={PADDING.left - 10} y={y + 4} className="team-trend-axis-label" textAnchor="end">
                  {tick}
                </text>
              </g>
            );
          })}

          {linePaths.map(({ key, points, path }) => (
            <g key={key}>
              <path d={path} className="team-trend-line" stroke={SERIES_CONFIG.find((s) => s.key === key)?.color} fill="none" />
              {points.map((point, index) => (
                <circle
                  key={`${key}-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={hoverIndex === index ? 5 : 3.5}
                  fill="#ffffff"
                  stroke={SERIES_CONFIG.find((s) => s.key === key)?.color}
                  strokeWidth={2}
                  className="team-trend-point"
                />
              ))}
            </g>
          ))}

          {hoverIndex != null && (
            <line
              x1={scaleX(hoverIndex)}
              y1={PADDING.top}
              x2={scaleX(hoverIndex)}
              y2={PADDING.top + innerHeight}
              className="team-trend-hover-line"
            />
          )}

          {merged.map((row, index) =>
            index % labelStep === 0 || index === merged.length - 1 ? (
              <text
                key={row.label}
                x={scaleX(index)}
                y={CHART_HEIGHT - 12}
                className="team-trend-x-label"
                textAnchor="middle"
              >
                {row.axisLabel}
              </text>
            ) : null
          )}
        </svg>
      </div>

      {activeRow && (
        <div className="team-trend-tooltip" role="status">
          <strong>{activeRow.displayLabel}</strong>
          {SERIES_CONFIG.map(({ key, label, color }) => (
            <div key={key} className="team-trend-tooltip-row">
              <span className="team-trend-tooltip-dot" style={{ background: color }} />
              <span>{label}</span>
              <span className="ms-auto fw-semibold">{activeRow[key] ?? 0}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamTrendChart;
